import React from 'react'
import { Box, Skeleton, Typography } from '@mui/material'
import ImageIcon from '@mui/icons-material/Image'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import ListAltIcon from '@mui/icons-material/ListAlt'

/**
 * Designed placeholder states for every widget type (#971). Purely visual —
 * no data is read here, this is what a widget looks like the instant it's
 * dropped on the canvas, before anyone has configured it. The quality bar is
 * "feels like Figma, not a grey box" (per the issue), so every case gets its
 * own composition rather than a shared generic box.
 */

const shimmerSx = {
  '& .MuiSkeleton-root': { transform: 'scale(1, 1)' },
}

function TitlePlaceholder() {
  return (
    <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
      <Typography variant="h4" sx={{ color: 'text.disabled', fontWeight: 700 }}>Section Title</Typography>
      <Typography variant="body1" sx={{ color: 'text.disabled', mt: 0.5 }}>Subtitle</Typography>
    </Box>
  )
}

function TextBoxPlaceholder() {
  return (
    <Box sx={{ p: 2, ...shimmerSx }}>
      <Skeleton variant="text" width="90%" height={16} />
      <Skeleton variant="text" width="75%" height={16} />
      <Skeleton variant="text" width="82%" height={16} />
      <Skeleton variant="text" width="45%" height={16} />
    </Box>
  )
}

function CalloutPlaceholder() {
  return (
    <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start', bgcolor: 'action.hover', borderRadius: 2, m: 1.5 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'action.selected', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <LightbulbIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="55%" height={18} />
        <Skeleton variant="text" width="90%" height={14} />
        <Skeleton variant="text" width="70%" height={14} />
      </Box>
    </Box>
  )
}

function ChartPlaceholder() {
  const bars = [40, 65, 30, 80, 55, 70]
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 100, borderLeft: '1px solid', borderBottom: '1px solid', borderColor: 'divider', px: 1, pb: 0.5 }}>
        {bars.map((h, i) => (
          <Box key={i} sx={{ flex: 1, height: `${h}%`, bgcolor: 'action.selected', borderRadius: '2px 2px 0 0' }} />
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1, textAlign: 'center' }}>
        Chart — not yet configured
      </Typography>
    </Box>
  )
}

function TablePlaceholder() {
  const cols = 4
  const rows = 4
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', bgcolor: 'action.hover' }}>
          {Array.from({ length: cols }).map((_, i) => (
            <Box key={i} sx={{ flex: 1, py: 1, px: 1, borderRight: i < cols - 1 ? '1px solid' : undefined, borderColor: 'divider' }}>
              <Skeleton variant="text" width="70%" height={12} />
            </Box>
          ))}
        </Box>
        {Array.from({ length: rows }).map((_, r) => (
          <Box key={r} sx={{ display: 'flex', borderTop: '1px solid', borderColor: 'divider' }}>
            {Array.from({ length: cols }).map((_, c) => (
              <Box key={c} sx={{ flex: 1, py: 0.75, px: 1, borderRight: c < cols - 1 ? '1px solid' : undefined, borderColor: 'divider' }}>
                <Skeleton variant="text" width={c === 0 ? '85%' : '50%'} height={10} />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function GenAiInsightsPlaceholder() {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AutoAwesomeIcon fontSize="small" sx={{ color: 'info.main', opacity: 0.6 }} />
        <Typography variant="caption" sx={{ color: 'info.main', opacity: 0.8, fontWeight: 600 }}>GEN AI INSIGHTS</Typography>
      </Box>
      <Box sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 1 } } }}>
        {[95, 88, 60].map((w, i) => (
          <Skeleton
            key={i}
            variant="text"
            width={`${w}%`}
            height={14}
            sx={{ animation: 'pulse 1.8s ease-in-out infinite', animationDelay: `${i * 0.2}s` }}
          />
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1.5 }}>
        Not yet configured — click ✏️ to set up
      </Typography>
    </Box>
  )
}

function AgentArtifactsPlaceholder() {
  return (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <SmartToyIcon sx={{ fontSize: 32, color: 'action.disabled', mb: 1 }} />
      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
        Agent artifacts will appear here
      </Typography>
    </Box>
  )
}

function KpiCardPlaceholder() {
  return (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="h3" sx={{ color: 'text.disabled', fontWeight: 700 }}>—</Typography>
      <Skeleton variant="text" width="60%" height={14} sx={{ mx: 'auto', mt: 0.5 }} />
    </Box>
  )
}

function ImagePlaceholder() {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ height: 120, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ImageIcon sx={{ fontSize: 36, color: 'action.disabled' }} />
      </Box>
    </Box>
  )
}

function VideoPlaceholder() {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ height: 120, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <PlayCircleIcon sx={{ fontSize: 40, color: 'action.disabled' }} />
      </Box>
    </Box>
  )
}

function TocPlaceholder() {
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ListAltIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>CONTENTS</Typography>
      </Box>
      {[1, 2, 3].map(i => (
        <Skeleton key={i} variant="text" width={`${70 - i * 8}%`} height={14} sx={{ mb: 0.5 }} />
      ))}
    </Box>
  )
}

function SpacerPlaceholder() {
  return (
    <Box
      sx={{
        m: 1.5,
        minHeight: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        opacity: 0.5,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: 1, textTransform: 'uppercase' }}>
        Spacer
      </Typography>
    </Box>
  )
}

const PLACEHOLDER_BY_SIDEBAR_TYPE: Record<string, React.FC> = {
  title_hero: TitlePlaceholder,
  text: TextBoxPlaceholder,
  callout: CalloutPlaceholder,
  chart: ChartPlaceholder,
  table: TablePlaceholder,
  multi_table_analysis: GenAiInsightsPlaceholder,
  agent_artifacts: AgentArtifactsPlaceholder,
  kpi_card: KpiCardPlaceholder,
  image: ImagePlaceholder,
  video: VideoPlaceholder,
  toc: TocPlaceholder,
  spacer: SpacerPlaceholder,
}

/** Map a stored `DashboardCell` back to the sidebar type the placeholder table keys on. */
export function sidebarTypeForCell(widgetType: string, viewMode?: string): string {
  if (widgetType === 'table_graph') return viewMode === 'table' ? 'table' : 'chart'
  return widgetType
}

export const WidgetPlaceholder: React.FC<{ widgetType: string; viewMode?: string }> = ({ widgetType, viewMode }) => {
  const sidebarType = sidebarTypeForCell(widgetType, viewMode)
  const Placeholder = PLACEHOLDER_BY_SIDEBAR_TYPE[sidebarType] || TextBoxPlaceholder
  return <Placeholder />
}

export default WidgetPlaceholder
